/**
 * ログイン・新規登録画面
 * Cognito メール・パスワード認証 + Google / Apple OAuth 認証
 */

import React, { useState, useEffect } from "react";
import { useAuth } from "../../../contexts/AuthContext";
import { PENDING_CONSENT_KEY } from "../../../contexts/UserProfileContext";
import { Button } from "../../ui/Button";
import { Mail, Lock, AlertCircle, Loader2, KeyRound, CheckCircle2 } from "lucide-react";

/**
 * 確認コード待ちのメールアドレス。
 * 確認コードを読むには利用者がメールアプリへ移動するため、戻ってきたときに
 * 画面状態だけだと確認フォームが消えて入力する場所が無くなる（2026-09-20 に実機で発生）。
 */
const PENDING_SIGNUP_EMAIL_KEY = "kakumon.pendingSignUpEmail";


/** 規約・プラポリへの同意文言（ログイン/新規登録の両モードに表示。第1条のみなし同意の実効性を担保） */
const TermsConsentNote: React.FC<{ className?: string }> = ({ className = "" }) => (
  <p className={`text-xs text-slate-500 dark:text-slate-400 leading-relaxed text-center ${className}`}>
    登録・ログインすることで
    <a href="/terms" className="text-blue-600 dark:text-blue-400 underline mx-0.5">利用規約</a>
    と
    <a href="/privacy" className="text-blue-600 dark:text-blue-400 underline mx-0.5">プライバシーポリシー</a>
    に同意したものとみなします。
  </p>
);

/**
 * Google アイコン（SVG）
 */
const GoogleIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      fill="#4285F4"
    />
    <path
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      fill="#34A853"
    />
    <path
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      fill="#FBBC05"
    />
    <path
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      fill="#EA4335"
    />
  </svg>
);

/**
 * Apple アイコン（SVG）
 * Apple Human Interface Guidelines 準拠: currentColor で黒地/白地どちらのボタンにも追従
 */
const AppleIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M16.365 1.43c0 1.14-.415 2.02-1.245 2.775-.895.83-1.955 1.31-3.075 1.22-.06-1.1.41-2.06 1.23-2.83.86-.81 2.03-1.29 3.09-1.165zM20.5 17.14c-.545 1.26-.805 1.82-1.51 2.94-.98 1.55-2.365 3.48-4.08 3.49-1.53.015-1.93-1-4.005-.99-2.075.01-2.51 1.005-4.04.99-1.71-.015-3.02-1.75-4-3.3C.335 17.09-.415 12.63 1.28 9.79c1.2-2.005 3.24-3.27 5.5-3.3 1.67-.03 3.24 1.14 4.005 1.14.76 0 2.65-1.41 4.475-1.2 0.76.03 2.9.31 4.28 2.32-.11.07-2.555 1.5-2.53 4.475.03 3.555 3.11 4.74 3.145 4.755-.03.09-.49 1.71-1.66 3.36z" />
  </svg>
);

type Mode = "login" | "register" | "confirm" | "forgot" | "reset";

/**
 * ログインページコンポーネント
 */
export const LoginPage: React.FC<{ initialMode?: "login" | "register" }> = ({ initialMode = "login" }) => {
  const { signIn, signUp, confirmSignUp, resendConfirmationCode, requestPasswordReset, confirmPasswordReset, signInWithGoogle, signInWithApple, isLoading, error } = useAuth();

  const [mode, setMode] = useState<Mode>(initialMode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [confirmCode, setConfirmCode] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [pendingEmail, setPendingEmail] = useState(() => {
    try {
      return localStorage.getItem(PENDING_SIGNUP_EMAIL_KEY) ?? "";
    } catch {
      return "";
    }
  });
  const [resendSuccess, setResendSuccess] = useState(false);
  // 登録直後だけ「受け付けました」を出す（戻り導線から来たときは出さない）
  const [justSignedUp, setJustSignedUp] = useState(false);
  const [resetCode, setResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [resetDone, setResetDone] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(true);

  // ログインフォーム送信
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    if (!email.trim()) { setFormError("メールアドレスを入力してください"); return; }
    if (!password.trim()) { setFormError("パスワードを入力してください"); return; }
    try {
      await signIn(email, password);
    } catch (err) {
      // 未確認のまま放置したアカウントは、ログインを試みるのが自然な導線。
      // メッセージを出して終わりにせず、そのまま確認コード入力へ送る
      if ((err as { code?: string })?.code === "UserNotConfirmedException") {
        try {
          localStorage.setItem(PENDING_SIGNUP_EMAIL_KEY, email);
        } catch { /* noop */ }
        setPendingEmail(email);
        setJustSignedUp(false);
        setMode("confirm");
      }
      /* それ以外は context の error を表示 */
    }
  };

  // 新規登録フォーム送信
  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    if (!email.trim()) { setFormError("メールアドレスを入力してください"); return; }
    if (password.length < 8) { setFormError("パスワードは8文字以上で入力してください"); return; }
    // Cognito 側のポリシーは RequireUppercase / Lowercase / Numbers / **Symbols** がすべて true。
    // 記号を条件に入れていなかったため、画面の条件を満たしても登録が弾かれていた（2026-09-20）
    if (!/[A-Z]/.test(password) || !/[a-z]/.test(password) || !/[0-9]/.test(password)) {
      setFormError("パスワードには大文字・小文字・数字をそれぞれ1文字以上含めてください");
      return;
    }
    if (!/[^A-Za-z0-9]/.test(password)) {
      setFormError("パスワードには記号（! @ # $ % など）を1文字以上含めてください");
      return;
    }
    if (password !== confirmPassword) { setFormError("パスワードが一致しません"); return; }
    try {
      await signUp(email, password);
      // メール確認前はAPIを呼べないため、初回ログイン後にUserProfileContextが保存する
      localStorage.setItem(PENDING_CONSENT_KEY, String(marketingConsent));
      try {
        localStorage.setItem(PENDING_SIGNUP_EMAIL_KEY, email);
      } catch { /* プライベートモード等では保持できないが致命ではない */ }
      setPendingEmail(email);
      setJustSignedUp(true);
      setMode("confirm");
    } catch (err) {
      // 「すでに登録されています」は、**本登録が済んでいない利用者にも出る**。
      // Cognito は未確認のユーザーが残っていても UsernameExistsException を返すため、
      // ここで確認コードを送り直せるか試し、送れたら確認画面へ送る。
      // 送れなければ本当に登録済み（＝ログインすべき）と判断する。
      if ((err as { code?: string })?.code === "UsernameExistsException") {
        try {
          await resendConfirmationCode(email);
          try {
            localStorage.setItem(PENDING_SIGNUP_EMAIL_KEY, email);
          } catch { /* noop */ }
          setPendingEmail(email);
          setJustSignedUp(false);
          setResendSuccess(true);
          setMode("confirm");
        } catch {
          setFormError(
            "このメールアドレスは登録済みです。ログイン画面からログインしてください。",
          );
        }
      }
      /* それ以外は context の error を表示 */
    }
  };

  // 確認コード送信
  const handleConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    if (!/^\d{6}$/.test(confirmCode)) { setFormError("6桁の確認コードを入力してください"); return; }
    try {
      await confirmSignUp(pendingEmail, confirmCode);
      try {
        localStorage.removeItem(PENDING_SIGNUP_EMAIL_KEY);
      } catch { /* noop */ }
      setPendingEmail("");
      // 確認完了 → ログインフォームへ（パスワードは保持しない）
      setMode("login");
      setPassword("");
      setConfirmCode("");
    } catch { /* error in context */ }
  };

  // パスワード再設定: コードの送付を依頼
  const handleForgot = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    if (!email.trim()) { setFormError("メールアドレスを入力してください"); return; }
    try {
      await requestPasswordReset(email);
      setPendingEmail(email);
      setMode("reset");
    } catch { /* error in context */ }
  };

  // パスワード再設定: コードと新しいパスワードを確定
  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    if (!/^\d{6}$/.test(resetCode)) { setFormError("6桁の確認コードを入力してください"); return; }
    if (newPassword.length < 8) { setFormError("パスワードは8文字以上で入力してください"); return; }
    if (!/[A-Z]/.test(newPassword) || !/[a-z]/.test(newPassword) || !/[0-9]/.test(newPassword)) {
      setFormError("パスワードには大文字・小文字・数字をそれぞれ1文字以上含めてください");
      return;
    }
    if (!/[^A-Za-z0-9]/.test(newPassword)) {
      setFormError("パスワードには記号（! @ # $ % など）を1文字以上含めてください");
      return;
    }
    try {
      await confirmPasswordReset(pendingEmail, resetCode, newPassword);
      setResetCode("");
      setNewPassword("");
      setPassword("");
      setResetDone(true);
      setMode("login");
    } catch { /* error in context */ }
  };

  // コード再送
  const handleResend = async () => {
    setResendSuccess(false);
    try {
      await resendConfirmationCode(pendingEmail);
      setResendSuccess(true);
    } catch { /* error in context */ }
  };

  /** コードやパスワードの入力に集中させたいモード（ブランド紹介を縮める） */
  const isTaskMode = mode === "confirm" || mode === "reset" || mode === "forgot";

  // モードを切り替えたら先頭へ戻す。スクロールした位置のままだと、
  // 切り替わった先の入力欄が画面外になることがある
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [mode]);

  const displayError = formError || error;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-200 dark:from-slate-900 dark:to-slate-800 flex items-center justify-center px-4 pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]">
      <div className="w-full max-w-md">
        {/* ロゴ */}
        {/* コード入力の画面では、ブランド紹介が縦を食って入力欄がファーストビューから外れる。
            作業中のモードでは見出しだけに縮める（2026-09-20 実機の指摘） */}
        {isTaskMode ? (
          <div className="flex items-center justify-center gap-2.5 mb-5">
            <img src="/logo.svg" alt="カクモン" className="w-8 h-8 rounded-lg" />
            <span className="text-lg font-bold text-slate-900 dark:text-white">カクモン</span>
          </div>
        ) : (
          <div className="text-center mb-8">
            <img src="/logo.svg" alt="カクモン" className="w-16 h-16 rounded-2xl shadow-sm mb-4 mx-auto" />
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">カクモン</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              資格学習プラットフォーム
            </p>
            <p className="text-xs text-slate-400 dark:text-slate-500 mt-2">
              2027年度の新試験に対応したAI生成問題も順次提供予定
            </p>
          </div>
        )}

        <div className={`bg-white dark:bg-slate-800 rounded-2xl shadow-sm dark:shadow-slate-900/20 border border-slate-200 dark:border-slate-600 ${isTaskMode ? "p-6" : "p-8"}`}>
          {/* エラーメッセージ */}
          {displayError && (
            <div className="flex items-start gap-3 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl mb-6">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700 dark:text-red-300">{displayError}</p>
            </div>
          )}

          {/* ===== ログインモード ===== */}
          {mode === "login" && (
            <>
              {resetDone && (
                <div className="flex items-start gap-2.5 p-3.5 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-xl mb-4">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 dark:text-emerald-400 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-emerald-800 dark:text-emerald-200">
                    <span className="font-bold">パスワードを再設定しました。</span>
                    <br />
                    新しいパスワードでログインしてください。
                  </p>
                </div>
              )}

              <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-6">ログイン</h2>

              <button
                type="button"
                onClick={signInWithGoogle}
                disabled={isLoading}
                className="w-full flex items-center justify-center gap-3 px-4 py-3 border border-slate-200 dark:border-slate-600 rounded-xl text-slate-700 dark:text-slate-300 font-medium text-sm bg-white dark:bg-slate-700 hover:bg-slate-50 dark:hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-slate-400 focus:ring-offset-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed mb-3"
                aria-label="Google アカウントでログイン"
              >
                <GoogleIcon className="w-5 h-5" />
                Google アカウントでログイン
              </button>

              <button
                type="button"
                onClick={signInWithApple}
                disabled={isLoading}
                className="w-full flex items-center justify-center gap-3 px-4 py-3 rounded-xl text-white font-medium text-sm bg-black hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-slate-400 focus:ring-offset-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed mb-6"
                aria-label="Apple でサインイン"
              >
                <AppleIcon className="w-5 h-5" />
                Appleでサインイン
              </button>

              <TermsConsentNote className="mb-6" />

              <div className="relative mb-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-slate-200 dark:border-slate-600" />
                </div>
                <div className="relative flex justify-center text-xs">
                  <span className="bg-white dark:bg-slate-800 px-3 text-slate-400 dark:text-slate-500">
                    またはメールアドレスでログイン
                  </span>
                </div>
              </div>

              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    メールアドレス
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 dark:text-slate-400" />
                    <input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="example@email.com"
                      autoComplete="email"
                      className="w-full pl-10 pr-4 py-3 border border-slate-200 dark:border-slate-600 rounded-xl text-slate-900 dark:text-white bg-white dark:bg-slate-700 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-slate-400 focus:border-transparent transition-all duration-200"
                      disabled={isLoading}
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    パスワード
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 dark:text-slate-400" />
                    <input
                      id="password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="パスワードを入力"
                      autoComplete="current-password"
                      className="w-full pl-10 pr-4 py-3 border border-slate-200 dark:border-slate-600 rounded-xl text-slate-900 dark:text-white bg-white dark:bg-slate-700 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-slate-400 focus:border-transparent transition-all duration-200"
                      disabled={isLoading}
                    />
                  </div>
                </div>

                <Button variant="primary" size="lg" type="submit" disabled={isLoading} className="w-full mt-2">
                  {isLoading ? (
                    <span className="flex items-center justify-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      ログイン中...
                    </span>
                  ) : "ログイン"}
                </Button>
              </form>

              <div className="mt-4 text-center">
                <button
                  type="button"
                  onClick={() => { setFormError(null); setMode("forgot"); }}
                  className="text-sm text-blue-600 dark:text-blue-400 font-medium hover:underline cursor-pointer min-h-[44px]"
                >
                  パスワードをお忘れですか？
                </button>
              </div>

              {/* ログインに失敗したときの逃げ道。
                  このユーザープールは PreventUserExistenceErrors が ENABLED のため、
                  **メール未確認でも「メールアドレスまたはパスワードが正しくありません」に丸められる**。
                  コード側で未確認を判別できないので、利用者が自分で選べる導線を出す（2026-09-20） */}
              {displayError && !pendingEmail && (
                <div className="mt-4 p-3 bg-slate-50 dark:bg-slate-700/40 border border-slate-200 dark:border-slate-600 rounded-xl">
                  <p className="text-sm text-slate-600 dark:text-slate-300">
                    メールの確認がお済みでない場合は、確認コードの入力に進めます。
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      if (!email.trim()) { setFormError("メールアドレスを入力してください"); return; }
                      setFormError(null);
                      setPendingEmail(email);
                      setJustSignedUp(false);
                      setMode("confirm");
                    }}
                    className="mt-2 min-h-[44px] w-full rounded-xl bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-200 text-sm font-bold cursor-pointer hover:opacity-90 transition-opacity"
                  >
                    確認コードを入力する
                  </button>
                </div>
              )}

              {/* 確認コード待ちのまま離脱した人の戻り道。
                  コードを読むためにメールアプリへ移動すると確認フォームが消えるため、
                  ここから必ず入力画面へ戻れるようにする */}
              {pendingEmail && (
                <div className="mt-5 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl">
                  <p className="text-sm text-amber-800 dark:text-amber-200">
                    <span className="font-medium">{pendingEmail}</span> の確認が未完了です。
                  </p>
                  <button
                    type="button"
                    onClick={() => { setFormError(null); setJustSignedUp(false); setMode("confirm"); }}
                    className="mt-2 min-h-[44px] w-full rounded-xl bg-amber-100 dark:bg-amber-900/40 text-amber-900 dark:text-amber-100 text-sm font-bold cursor-pointer hover:opacity-90 transition-opacity"
                  >
                    確認コードを入力する
                  </button>
                </div>
              )}

              <div className="mt-6 text-center">
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  アカウントをお持ちでない方は{" "}
                  <button
                    onClick={() => { setFormError(null); setMode("register"); }}
                    className="text-blue-600 dark:text-blue-400 font-medium hover:underline"
                  >
                    新規登録
                  </button>
                </p>
              </div>
            </>
          )}

          {/* ===== 新規登録モード ===== */}
          {mode === "register" && (
            <>
              <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-6">新規登録（無料）</h2>

              <form onSubmit={handleRegister} className="space-y-4">
                <div>
                  <label htmlFor="reg-email" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    メールアドレス
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 dark:text-slate-400" />
                    <input
                      id="reg-email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="example@email.com"
                      autoComplete="email"
                      className="w-full pl-10 pr-4 py-3 border border-slate-200 dark:border-slate-600 rounded-xl text-slate-900 dark:text-white bg-white dark:bg-slate-700 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-slate-400 focus:border-transparent transition-all duration-200"
                      disabled={isLoading}
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="reg-password" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    パスワード
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 dark:text-slate-400" />
                    <input
                      id="reg-password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="8文字以上（大文字・小文字・数字・記号を含む）"
                      autoComplete="new-password"
                      className="w-full pl-10 pr-4 py-3 border border-slate-200 dark:border-slate-600 rounded-xl text-slate-900 dark:text-white bg-white dark:bg-slate-700 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-slate-400 focus:border-transparent transition-all duration-200"
                      disabled={isLoading}
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="reg-confirm" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    パスワード（確認）
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 dark:text-slate-400" />
                    <input
                      id="reg-confirm"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="もう一度入力してください"
                      autoComplete="new-password"
                      className="w-full pl-10 pr-4 py-3 border border-slate-200 dark:border-slate-600 rounded-xl text-slate-900 dark:text-white bg-white dark:bg-slate-700 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-slate-400 focus:border-transparent transition-all duration-200"
                      disabled={isLoading}
                    />
                  </div>
                </div>

                <label className="flex items-start gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={marketingConsent}
                    onChange={(e) => setMarketingConsent(e.target.checked)}
                    disabled={isLoading}
                    className="mt-0.5 w-4 h-4 rounded border-slate-200 dark:border-slate-600 accent-blue-500 dark:accent-blue-400"
                  />
                  <span className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
                    試験制度の変更アラートや新機能のお知らせ、ユーザーインタビュー等のご案内をメールで受け取る
                  </span>
                </label>

                <TermsConsentNote />

                <Button variant="primary" size="lg" type="submit" disabled={isLoading} className="w-full mt-2">
                  {isLoading ? (
                    <span className="flex items-center justify-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      登録中...
                    </span>
                  ) : "新規登録"}
                </Button>
              </form>

              <div className="mt-6 text-center">
                <button
                  onClick={() => { setFormError(null); setMode("login"); }}
                  className="text-sm text-slate-500 dark:text-slate-400 hover:underline"
                >
                  ← ログインに戻る
                </button>
              </div>
            </>
          )}

          {/* ===== 確認コード入力モード ===== */}
          {/* ===== パスワード再設定: メール入力 ===== */}
          {mode === "forgot" && (
            <>
              <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-2">パスワードの再設定</h2>
              <p className="text-sm text-slate-600 dark:text-slate-300 mb-6">
                登録したメールアドレスに、再設定用の6桁コードを送ります。
              </p>
              <form onSubmit={handleForgot} className="space-y-4">
                <div>
                  <label htmlFor="forgot-email" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    メールアドレス
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 dark:text-slate-400" />
                    <input
                      id="forgot-email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="example@email.com"
                      autoComplete="email"
                      className="w-full pl-10 pr-4 py-3 border border-slate-200 dark:border-slate-600 rounded-xl text-slate-900 dark:text-white bg-white dark:bg-slate-700 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-slate-400 focus:border-transparent transition-all duration-200"
                      disabled={isLoading}
                    />
                  </div>
                </div>
                <Button variant="primary" size="lg" type="submit" disabled={isLoading} className="w-full mt-2">
                  {isLoading ? "送信中..." : "再設定コードを送る"}
                </Button>
              </form>
              <div className="mt-6 text-center">
                <button
                  onClick={() => { setFormError(null); setMode("login"); }}
                  className="text-sm text-blue-600 dark:text-blue-400 font-medium hover:underline cursor-pointer min-h-[44px]"
                >
                  ログインに戻る
                </button>
              </div>
            </>
          )}

          {/* ===== パスワード再設定: コードと新パスワード ===== */}
          {mode === "reset" && (
            <>
              <p className="text-xs font-bold tracking-wide text-blue-700 dark:text-blue-300 mb-1">あと1ステップ</p>
              <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-2">
                新しいパスワードを設定してください
              </h2>
              <p className="text-sm text-slate-600 dark:text-slate-300 mb-2">
                <span className="font-medium text-slate-900 dark:text-slate-100">{pendingEmail}</span>{" "}
                に6桁のコードを送りました。
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-400 mb-6">
                届かない場合は迷惑メールフォルダもご確認ください。
              </p>
              <form onSubmit={handleReset} className="space-y-4">
                <div>
                  <label htmlFor="reset-code" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    確認コード（6桁）
                  </label>
                  <div className="relative">
                    <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 dark:text-slate-400" />
                    <input
                      id="reset-code"
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      value={resetCode}
                      onChange={(e) => setResetCode(e.target.value.replace(/\D/g, ""))}
                      placeholder="123456"
                      autoComplete="one-time-code"
                      autoFocus
                      className="w-full pl-10 pr-4 py-3 border border-slate-200 dark:border-slate-600 rounded-xl text-slate-900 dark:text-white bg-white dark:bg-slate-700 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-slate-400 focus:border-transparent transition-all duration-200 text-center text-lg tracking-widest font-mono"
                      disabled={isLoading}
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="new-password" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    新しいパスワード
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 dark:text-slate-400" />
                    <input
                      id="new-password"
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="8文字以上（大文字・小文字・数字・記号を含む）"
                      autoComplete="new-password"
                      className="w-full pl-10 pr-4 py-3 border border-slate-200 dark:border-slate-600 rounded-xl text-slate-900 dark:text-white bg-white dark:bg-slate-700 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-slate-400 focus:border-transparent transition-all duration-200"
                      disabled={isLoading}
                    />
                  </div>
                </div>
                <Button variant="primary" size="lg" type="submit" disabled={isLoading} className="w-full mt-2">
                  {isLoading ? "設定中..." : "パスワードを再設定する"}
                </Button>
              </form>
              <div className="mt-6 text-center">
                <button
                  onClick={() => { setFormError(null); setMode("login"); }}
                  className="text-sm text-blue-600 dark:text-blue-400 font-medium hover:underline cursor-pointer min-h-[44px]"
                >
                  ログインに戻る
                </button>
              </div>
            </>
          )}

          {mode === "confirm" && (
            <>
              {justSignedUp && (
                <div className="flex items-start gap-2.5 p-3.5 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-xl mb-4">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 dark:text-emerald-400 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-emerald-800 dark:text-emerald-200">
                    <span className="font-bold">登録を受け付けました。</span>
                    <br />
                    確認コードをメールで送信しました。
                  </p>
                </div>
              )}

              <p className="text-xs font-bold tracking-wide text-blue-700 dark:text-blue-300 mb-1">
                あと1ステップ
              </p>
              <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-2">
                確認コードを入力してください
              </h2>
              <p className="text-sm text-slate-600 dark:text-slate-300 mb-2">
                <span className="font-medium text-slate-900 dark:text-slate-100">{pendingEmail}</span>{" "}
                に6桁のコードを送りました。入力すると登録が完了します。
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-400 mb-6">
                届かない場合は迷惑メールフォルダもご確認ください。下の「コードを再送する」から送り直せます。
              </p>

              {resendSuccess && (
                <div className="p-3 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-xl mb-4 text-sm text-emerald-700 dark:text-emerald-300">
                  確認コードを再送しました。メールをご確認ください。
                </div>
              )}

              <form onSubmit={handleConfirm} className="space-y-4">
                <div>
                  <label htmlFor="confirm-code" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    確認コード（6桁）
                  </label>
                  <div className="relative">
                    <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 dark:text-slate-400" />
                    <input
                      id="confirm-code"
                      type="text"
                      inputMode="numeric"
                      pattern="\d{6}"
                      maxLength={6}
                      value={confirmCode}
                      onChange={(e) => setConfirmCode(e.target.value.replace(/\D/g, ""))}
                      placeholder="123456"
                      autoComplete="one-time-code"
                      autoFocus
                      className="w-full pl-10 pr-4 py-3 border border-slate-200 dark:border-slate-600 rounded-xl text-slate-900 dark:text-white bg-white dark:bg-slate-700 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-slate-400 focus:border-transparent transition-all duration-200 text-center text-lg tracking-widest font-mono"
                      disabled={isLoading}
                    />
                  </div>
                </div>

                <Button variant="primary" size="lg" type="submit" disabled={isLoading} className="w-full mt-2">
                  {isLoading ? (
                    <span className="flex items-center justify-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      確認中...
                    </span>
                  ) : "確認する"}
                </Button>
              </form>

              <div className="mt-4 text-center space-y-2">
                <button
                  onClick={handleResend}
                  disabled={isLoading}
                  className="text-sm text-blue-600 dark:text-blue-400 hover:underline disabled:opacity-50"
                >
                  コードを再送する
                </button>
                <div>
                  <button
                    onClick={() => { setFormError(null); setMode("login"); }}
                    className="text-sm text-slate-500 dark:text-slate-400 hover:underline"
                  >
                    ← ログインに戻る
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        <p className="text-center text-xs text-slate-500 dark:text-slate-500 mt-6">
          © 2026 カクモン
        </p>
      </div>
    </div>
  );
};
